import { ApolloCache, NormalizedCacheObject } from '@apollo/client';
import {
  BucketType,
  UserBucketAssociationCacheFragment,
  UserBucketAssociationCacheFragmentDoc,
} from '@/gql/graphql';

/** Update normalized membership lists without requiring a complete User query. */
export class ApolloCacheUtils {
  public static addUserBucket(
    cache: ApolloCache<NormalizedCacheObject>,
    userId: number,
    bucket: BucketType
  ): void {
    const assoc = bucket.usersAssociated?.find((a) => a?.userId === userId);
    if (!assoc) return;

    const assocRef = cache.writeFragment<UserBucketAssociationCacheFragment>({
      fragment: UserBucketAssociationCacheFragmentDoc,
      data: {
        ...assoc,
        bucket: { __typename: bucket.__typename, id: bucket.id },
      },
    });
    if (!assocRef) return;

    cache.modify({
      id: cache.identify({ __typename: 'UserType', id: userId }),
      fields: {
        bucketsAssociated(existing = [], { readField }) {
          if (
            existing.some(
              (ref: typeof assocRef) => readField('bucketId', ref) === bucket.id
            )
          ) {
            return existing;
          }
          return [...existing, assocRef];
        },
      },
    });
  }

  public static removeUserBucket(
    cache: ApolloCache<NormalizedCacheObject>,
    userId: number,
    bucketId: string | number
  ): void {
    cache.modify({
      id: cache.identify({ __typename: 'UserType', id: userId }),
      fields: {
        bucketsAssociated(existing = [], { readField }) {
          return existing.filter(
            (ref: { __ref: string }) =>
              String(readField('bucketId', ref)) !== String(bucketId)
          );
        },
      },
    });
    const bucketRef = cache.identify({
      __typename: 'BucketType',
      id: bucketId,
    });
    if (bucketRef) cache.evict({ id: bucketRef });
    cache.gc();
  }
}
